import getConfig from 'next/config';
import { withRouter } from 'next/router';

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { Router } from '../../../routes';
import { getLocales } from '../../../utils/locales';
import withI18n from '../../../utils/withI18n';
import { eraseCookie, createCookie } from '../../../utils/cookies-browser';

import FlexBox from '../../../components/FlexBox';
import Logo from '../Logo';
import { NavLi, NavList, NavListItem } from '../../../components/Nav';
import Dropdown from '../../../components/Dropdown';
import Icon from '../../../components/Icon';

const {
  publicRuntimeConfig: {
    routePrefix: ROUTE_PREFIX,
  },
} = getConfig();

const prefixLink = uri => ROUTE_PREFIX + uri;

const StyledHeader = styled.header`
  height: 48px;
  font-size: 13px;
  white-space: nowrap;
  user-select: none;

  .menu-langs {
    flex-direction: column;
    width: auto;
    left: 0;

    li {
      height: 45px;
      flex: initial;
      max-width: inherit;
      width: 100%;
    }
  }
`;

const HeaderNav = styled(FlexBox)`
  line-height: 1.75;
  .trunk {
    width: 134px;
    border: none !important;
    box-shadow: none !important;
   }
`;

/* eslint-disable import/no-unresolved */
const HeaderNavWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 48px;
  z-index: 99;
  background: #262626 url(${require('../../../components/Header/header_bg.png')}) repeat-x left center;
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  margin: auto;
`;
/* eslint-enable import/no-unresolved */

const SelectedLocale = styled.span`
  color: #fff;
  padding: 0 16px;

  .choose-lang {
    margin: 0 5px 0 6px;
  }
  .icon-arrow-d {
    transform: scale(0.7);
  }
`;

const PopUpListItem = styled(NavListItem)`
  line-height: 36px;
`;

const LocaleListItem = styled(PopUpListItem)`
  box-sizing: border-box;
  flex: 1 0 50%;
  max-width: 50%;
  width: 50%;

  & > a {
    position: relative;
    border-bottom: 1px solid #262626;
    padding-left: 25px;
    &.selected {
      padding-left: 28px;
    }
    & .iconfont {
      margin-right: 8px;
      position: absolute;
      left: 5px;
      top: 50%;
      transform: translateY(-50%);
    }
  }
  &:nth-child(odd) {
    border-right: 1px solid #262626;
    &:last-child {
      margin-right: auto;
    }
  }
`;

const HeaderNavItem = styled(NavLi).attrs(props => ({
  'role': props.menuitem,
}))`
  position: relative;
  & > * {
    padding: 0 16px;
    line-height: 100%;
  }
  &:hover:not(.logo-item) {
    background-color: #333;
    cursor: pointer;
  }
  &:hover >.iconfont {
    color: #fff;
  }
  @media screen and (max-width:1366px) {
    >a{padding: 0 10px;}
  }

`;

const ModeList = styled(NavList)`
  display: ${({ hide }) => (hide ? 'none' : 'flex')};
  left: 0;
  @supports(-ms-scroll-limit: 0) {
    /* bad */
    height: 70px;
  }
`;

const LocaleList = styled(ModeList)`
  left: auto;
  right: 0;
  width: 250px;
`;

const sharedPropTypes = {
  i18n: PropTypes.object,
  locale: PropTypes.string,
  locales: PropTypes.object,
  hide: PropTypes.bool.isRequired,
  onMouseLeave: PropTypes.func.isRequired,
  getLocaleLink: PropTypes.func,
};

const defaultProps = {
  i18n: {},
  locale: 'en',
  getLocaleLink: () => {},
};

const _onLocaleItemClick = (props, _locale = 'en') => ev => {
  ev.preventDefault();
  ev.stopPropagation();
  props.onMouseLeave();
  const href = props.getLocaleLink(_locale);
  eraseCookie('lang');
  createCookie('lang', _locale.toUpperCase());
  Router.replaceRoute(href);
};

const LocaleMenu = props => (
  <Dropdown
    component={LocaleList}
    keys={Object.keys(props.locales)}
    isOpen={!props.hide}
    onMouseLeave={props.onMouseLeave}
    {...props}
  >
    {({ key: _locale, index }) => (
      <LocaleListItem key={_locale}>
        <a
          href={props.getLocaleLink(_locale)}
          onClick={_onLocaleItemClick(props, _locale)}
          onKeyPress={_onLocaleItemClick(props, _locale)}
          className={props.locale === _locale ? 'selected' : ''}
          role="button"
          tabIndex={index}
        >
          {props.locale === _locale ? <Icon type="gou" color="#e8b342" /> : null}
          {props.locales[_locale]}
        </a>
      </LocaleListItem>
    )}
  </Dropdown>
);

LocaleMenu.propTypes = sharedPropTypes;
LocaleMenu.defaultProps = defaultProps;

const mapStateToProps = ({ userInfo, selectedPair }) => (
  { userInfo, selectedPair }
);

export default
@withI18n
@withRouter
@connect(mapStateToProps)
class Header extends PureComponent {
  static propTypes = {
    i18n: PropTypes.object,
    locale: PropTypes.string.isRequired,
    locales: PropTypes.object,
    both: PropTypes.array,
    router: PropTypes.object,
    userInfo: PropTypes.object,
    selectedPair: PropTypes.string,
  };

  static defaultProps = {
    i18n: {},
    both: ['BTC', 'USDT'], // TODO: improve
    locales: getLocales(),
    router: { asPath: '/' },
    userInfo: {},
    selectedPair: 'BTC_USDT',
  };

  constructor(props) {
    super(props);
    this.state = {
      localeMenuPopped: false,
    };
  }

  static getDerivedStateFromProps({ locale }) {
    return {
      isShowGoogleTranslate: locale !== 'cn',
    };
  }

  _getLocaleLink = locale => {
    const { router } = this.props;
    const matcher = /\/[a-z]{0,2}(\/|$)/;
    // does current route not contain locale? then go home
    if (!matcher.test(router.asPath)) {
      return prefixLink(`/${locale}`);
    }
    // current route contains locale, replace it
    return router.asPath
      .replace(matcher, `/${locale}/`)
      .replace('//', '/');
  };

  _popUpLocaleMenu = bool => {
    this.setState({ localeMenuPopped: bool });
  };

  render() {
    const {
      locale, locales,
    } = this.props;
    const {
      localeMenuPopped,
    } = this.state;
    return (
      <StyledHeader direction="column" wrap="nowrap">
        <HeaderNavWrapper>
          <HeaderNav as="ul">
            <HeaderNavItem className="logo-item">
              <Logo locale={locale} isH5={true} />
            </HeaderNavItem>
          </HeaderNav>
          <HeaderNav as="ul" justify="flex-end">
            <HeaderNavItem
              role="menu"
              aria-expanded={localeMenuPopped}
              onClick={() => this._popUpLocaleMenu(true)}
              // onMouseEnter={() => this._popUpLocaleMenu(true)}
              onBlur={this._popUpLocaleMenu}
              className="trunk"
            >
              <SelectedLocale>
                <Icon type="diqiu" />
                <span className="choose-lang">{locales[locale]}</span>
                <Icon type="arrow-d" size="12" />
              </SelectedLocale>
              <LocaleMenu
                className="menu-langs"
                hide={!localeMenuPopped}
                locale={locale}
                locales={locales}
                onMouseLeave={this._popUpLocaleMenu}
                getLocaleLink={this._getLocaleLink}
              />
            </HeaderNavItem>
          </HeaderNav>
        </HeaderNavWrapper>
      </StyledHeader>
    );
  }
}
